import React, { useState } from "react";
import styled from "styled-components";
import { useGetOrdersQuery, useManualConfirmOrderMutation, useGetProfileQuery } from "../store/trader-api";
import { useNavigate } from "react-router-dom";
import { AntDateRangePicker } from "../components/AntDateRangePicker";

interface FilterState {
  status: string;
  search: string;
  bank: string;
  method: string;
  amountMin: string;
  amountMax: string;
  usdtMin: string;
  usdtMax: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
}

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
`;

const FiltersContainer = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-divider);
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 0.5rem;
  }
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    padding: 0.25rem 0;
  }
`;

const FiltersToggleIcon = styled.span<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
    color: var(--text-secondary);
    transition: transform 0.2s ease;
    transform: rotate(${(props) => (props.isOpen ? '180deg' : '0deg')});
  }
`;

const CollapsibleFilterBody = styled.div<{ isOpen: boolean }>`
  @media (max-width: 768px) {
    max-height: ${(props) => (props.isOpen ? '500px' : '0')};
    overflow: hidden;
    transition: max-height 0.3s ease;
    margin-top: ${(props) => (props.isOpen ? '0.75rem' : '0')};
  }
`;

const FiltersTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ClearFilters = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--text-primary);
  }
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    gap: 0.375rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const FilterTag = styled.div<{
  isActive: boolean;
  isExpanded?: boolean;
  hasContent?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-divider);
  position: relative;
  min-width: ${(props) => (props.isExpanded ? "200px" : "auto")};
  width: ${(props) => (props.isExpanded ? "fit-content" : "auto")};
  max-width: ${(props) => (props.isExpanded ? "400px" : "none")};
  min-height: 40px;
  height: ${(props) => (props.isExpanded ? "auto" : "40px")};
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: ${(props) => (props.isExpanded ? "visible" : "hidden")};
  flex-shrink: ${(props) => (props.isExpanded ? "0" : "1")};
  white-space: ${(props) => (props.isExpanded ? "normal" : "nowrap")};

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-input);
  }

  @media (max-width: 768px) {
    min-width: ${(props) => (props.isExpanded ? "280px" : "auto")};
    max-width: ${(props) => (props.isExpanded ? "100%" : "none")};
    flex-basis: ${(props) => (props.isExpanded ? "100%" : "auto")};
  }
`;

const DateFilterTag = styled.div<{
  isActive: boolean;
  isExpanded?: boolean;
  hasContent?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-divider);
  position: relative;
  min-width: ${(props) => (props.isExpanded ? "300px" : "auto")};
  width: ${(props) => (props.isExpanded ? "fit-content" : "auto")};
  max-width: ${(props) => (props.isExpanded ? "500px" : "none")};
  min-height: 40px;
  height: ${(props) => (props.isExpanded ? "auto" : "40px")};
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: ${(props) => (props.isExpanded ? "visible" : "hidden")};
  flex-shrink: ${(props) => (props.isExpanded ? "0" : "1")};
  white-space: ${(props) => (props.isExpanded ? "normal" : "nowrap")};

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-input);
  }

  @media (max-width: 768px) {
    min-width: ${(props) => (props.isExpanded ? "100%" : "auto")};
    max-width: 100%;
    flex-basis: ${(props) => (props.isExpanded ? "100%" : "auto")};
  }

  @media (max-width: 480px) {
    min-width: ${(props) => (props.isExpanded ? "calc(100vw - 2rem)" : "auto")};
  }
`;

const FilterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const FilterInput = styled.input`
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  padding: 0.125rem 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 80px;
  height: 28px;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    background: transparent;
  }
`;

const FilterSelect = styled.select`
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  padding: 0.125rem 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  min-width: 100px;
  height: 28px;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;

  &:focus {
    outline: none;
    background: transparent;
  }

  option {
    background: var(--bg-primary);
    color: var(--text-primary);
  }
`;

const FilterValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const CloseIcon = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.125rem;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;

const AddFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px dashed var(--border-input);
  background: transparent;
  color: var(--text-muted);
  transition: all 0.2s;
  height: 40px;
  box-sizing: border-box;

  &:hover {
    background: var(--bg-primary);
    border-color: var(--text-muted);
    color: var(--text-primary);
  }
`;

const OrdersCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
`;

const TableContainer = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    &::-webkit-scrollbar {
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: var(--bg-secondary);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--scrollbar-thumb);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: var(--scrollbar-thumb-hover);
    }
  }
`;

/* ====== Mobile Order Cards ====== */
const MobileOrdersList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1px;
    padding: 0;
    background: var(--border-color);
  }
`;

const MobileOrderCard = styled.div`
  background: var(--bg-card);
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background 0.1s;

  &:active {
    background: var(--bg-primary);
  }
`;

const MobileOrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const MobileOrderId = styled.span`
  font-family: 'SF Mono', 'Monaco', monospace;
  font-size: 0.6875rem;
  color: var(--accent-primary);
  font-weight: 600;
`;

const MobileOrderBody = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const MobileOrderField = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const MobileOrderLabel = styled.span`
  font-size: 0.5625rem;
  color: var(--text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

const MobileOrderValue = styled.span`
  font-size: 0.75rem;
  color: var(--text-primary);
  font-weight: 600;
  white-space: nowrap;
`;

const MobileOrderFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem;
`;

const TableHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-divider);

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const TableTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const TableRefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-divider);
  border-radius: 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-input);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RefreshSvgIcon = styled.svg<{ spinning?: boolean }>`
  width: 16px;
  height: 16px;
  animation: ${(props) =>
    props.spinning ? "spin 1s linear infinite" : "none"};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* Минимальная ширина для корректного отображения всех колонок */

  @media (max-width: 480px) {
    min-width: 1000px; /* На очень маленьких экранах увеличиваем минимальную ширину */
  }
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 1rem 1.5rem;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-divider);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;

  &:first-child {
    padding-left: 1.5rem;
  }

  &:last-child {
    padding-right: 1.5rem;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background: var(--bg-primary);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-divider);
  }
`;

const TableCell = styled.td`
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  vertical-align: middle;

  &:first-child {
    padding-left: 1.5rem;
  }

  &:last-child {
    padding-right: 1.5rem;
  }
`;

const OrderId = styled.span`
  font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
  font-size: 0.8125rem;
  color: var(--accent-primary);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  @media (max-width: 768px) {
    padding: 0.125rem 0.5rem;
    font-size: 0.5625rem;
  }

  ${(props) => {
    switch (props.status) {
      case "COMPLETED":
        return `
          background: var(--status-success-bg);
          color: var(--status-success-text);
          border: 1px solid var(--alert-success-border);
        `;
      case "PENDING":
        return `
          background: var(--status-pending-bg);
          color: var(--status-pending-text);
          border: 1px solid var(--alert-warning-border);
        `;
      case "PROCESSING":
        return `
          background: var(--alert-info-bg);
          color: var(--alert-info-text);
          border: 1px solid var(--alert-info-border);
        `;
      case "CANCELLED":
        return `
          background: var(--alert-error-bg);
          color: var(--accent-error);
          border: 1px solid var(--alert-error-border);
        `;
      case "FAILED":
        return `
          background: var(--alert-error-bg);
          color: var(--accent-error);
          border: 1px solid var(--alert-error-border);
        `;
      case "EXPIRED":
        return `
          background: var(--bg-secondary);
          color: var(--text-muted);
          border: 1px solid var(--border-input);
        `;
      default:
        return `
          background: var(--bg-secondary);
          color: var(--text-muted);
          border: 1px solid var(--border-input);
        `;
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-muted);
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-muted);
  text-align: center;

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
    color: var(--text-primary);
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const ConfirmButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-divider);
  border-radius: 0.375rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--border-input);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const calculateTimeLeft = (expiresAt: string | null) => {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt).getTime();
  const now = new Date().getTime();
  const difference = expiry - now;

  if (difference <= 0) {
    return null;
  }

  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const CountdownTimer: React.FC<{ expiresAt: string | null }> = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState<string | null>(
    calculateTimeLeft(expiresAt)
  );

  React.useEffect(() => {
    const timer = setInterval(() => {
      const remaining = calculateTimeLeft(expiresAt);
      setTimeLeft(remaining);
      if (!remaining) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!timeLeft) return <span>—</span>;

  return <span style={{ color: "var(--accent-error)", fontWeight: 600 }}>{timeLeft}</span>;
};

const Orders: React.FC = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FilterState>({
    status: "",
    search: "",
    bank: "",
    method: "",
    amountMin: "",
    amountMax: "",
    usdtMin: "",
    usdtMax: "",
    dateFrom: "",
    dateTo: "",
    timeFrom: "",
    timeTo: "",
  });

  const [page, setPage] = useState(1);
  const limit = 20;

  const [tempFilters, setTempFilters] = useState<FilterState>(filters);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const [confirmingOrderId, setConfirmingOrderId] = useState<string | null>(null);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  // Получаем профиль для проверки canConfirmManually
  const { data: profile } = useGetProfileQuery();
  const [manualConfirm] = useManualConfirmOrderMutation();

  // Функция для объединения даты и времени
  const combineDateTime = (date: string, time: string) => {
    if (!date) return undefined;
    if (!time) return date;
    return `${date}T${time}:00`;
  };

  const {
    data: ordersData,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetOrdersQuery({
    status: filters.status || undefined,
    search: filters.search || undefined,
    bank: filters.bank || undefined,
    method: filters.method || undefined,
    amountMin: filters.amountMin ? Number(filters.amountMin) : undefined,
    amountMax: filters.amountMax ? Number(filters.amountMax) : undefined,
    usdtMin: filters.usdtMin ? Number(filters.usdtMin) : undefined,
    usdtMax: filters.usdtMax ? Number(filters.usdtMax) : undefined,
    dateFrom: combineDateTime(filters.dateFrom, filters.timeFrom),
    dateTo: combineDateTime(filters.dateTo, filters.timeTo),
    page,
    limit,
  });

  const orders = Array.isArray(ordersData)
    ? ordersData
    : ordersData?.data || [];

  // Подсчет активных фильтров
  const activeFiltersCount = (() => {
    let count = 0;

    // Считаем обычные фильтры
    if (filters.status) count++;
    if (filters.search) count++;
    if (filters.bank) count++;
    if (filters.method) count++;
    if (filters.amountMin || filters.amountMax) count++;
    if (filters.usdtMin || filters.usdtMax) count++;

    // Период (дата + время) считается как один фильтр
    if (filters.dateFrom || filters.dateTo) count++;

    return count;
  })();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("ru-RU", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatUSDT = (amount: number) => {
    return `${amount.toFixed(2)} USDT`;
  };

  const formatRUB = (amount: number) => {
    return `${amount.toFixed(2)} ₽`;
  };

  const getPaymentMethod = (order: any) => {
    // Сначала проверяем прямое поле method
    if (order.method) {
      const method = order.method.toUpperCase();
      if (method === "SBP") return "СБП";
      if (method === "CARD" || method === "CARD_NUMBER") return "Карта";
      if (method === "ACCOUNT_TRANSFER" || method === "ACCOUNT") return "Перевод на счёт";
      if (method === "TRANSGOV") return "TransGov";
      return order.method;
    }
    // Обратная совместимость с флагами
    if (order.isSbp) return "СБП";
    if (order.phoneNumber || order.sbpPhoneNumber) return "СБП";
    if (order.isAccountTransfer) return "Перевод на счёт";
    if (order.isTransGov) return "TransGov";
    if (order.cardNumber) return "Карта";
    if (order.accountNumber) return "Счёт";
    return "—";
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Ожидает";
      case "PROCESSING": // Для обратной совместимости - показываем как Ожидание
        return "Ожидание";
      case "COMPLETED":
        return "Завершен";
      case "CANCELLED":
        return "Отменен";
      case "FAILED":
        return "Ошибка";
      case "EXPIRED":
        return "Истек";
      default:
        return status;
    }
  };

  const getMethodText = (method: string) => {
    switch (method.toLowerCase()) {
      case "sbp":
        return "СБП";
      case "card":
      case "card_number":
        return "Карта";
      case "account_transfer":
        return "Перевод на счёт";
      case "transgov":
        return "TransGov";
      default:
        return method;
    }
  };

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleManualConfirm = async (orderId: string) => {
    if (!window.confirm("Вы подтверждаете, что получили средства и хотите вручную завершить ордер?")) {
      return;
    }
    setConfirmingOrderId(orderId);
    try {
      await manualConfirm(orderId).unwrap();
      alert("Ордер успешно подтверждён");
      refetch();
    } catch (err) {
      console.error("Ошибка при ручном подтверждении:", err);
      alert("Не удалось подтвердить ордер");
    } finally {
      setConfirmingOrderId(null);
    }
  };

  const toggleFilterExpansion = (filterKey: string) => {
    if (expandedFilter === filterKey) {
      setExpandedFilter(null);
      setTempFilters(filters);
    } else {
      setExpandedFilter(filterKey);
      setTempFilters(filters);
    }
  };

  const handleFilterBlur = (e: React.FocusEvent) => {
    // Не обрабатываем blur для фильтра дат - он управляется автоматически
    if (expandedFilter === "date") {
      return;
    }

    // Задержка для проверки фокуса, чтобы дать время другим элементам получить фокус
    setTimeout(() => {
      const activeElement = document.activeElement;
      const currentTarget = e.currentTarget;

      // Проверяем на null
      if (!activeElement || !currentTarget) {
        return;
      }

      // Проверяем, находится ли активный элемент внутри текущего фильтра
      if (currentTarget.contains(activeElement)) {
        return;
      }

      // Закрываем фильтр только если фокус ушел за пределы
      setFilters(tempFilters);
      setExpandedFilter(null);
    }, 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setFilters(tempFilters);
      setExpandedFilter(null);
    }
    if (e.key === "Escape") {
      setTempFilters(filters);
      setExpandedFilter(null);
    }
  };

  const removeFilter = (key: keyof FilterState) => {
    const newFilters = { ...filters, [key]: "" };
    setFilters(newFilters);
    setTempFilters(newFilters);
    setExpandedFilter(null);
  };

  const clearAllFilters = () => {
    const emptyFilters: FilterState = {
      status: "",
      search: "",
      bank: "",
      method: "",
      amountMin: "",
      amountMax: "",
      usdtMin: "",
      usdtMax: "",
      dateFrom: "",
      dateTo: "",
      timeFrom: "",
      timeTo: "",
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setPage(1);
    setExpandedFilter(null);
  };

  // Debounce ref для сохранения между рендерами
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout>();

  // Автоматическое применение фильтров при изменении tempFilters для дат
  React.useEffect(() => {
    if (expandedFilter === "date") {
      // Очищаем предыдущий таймер
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Устанавливаем новый таймер
      debounceTimeoutRef.current = setTimeout(() => {
        setFilters(tempFilters);
      }, 500);
    }

    // Очистка при unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    tempFilters.dateFrom,
    tempFilters.dateTo,
    tempFilters.timeFrom,
    tempFilters.timeTo,
    expandedFilter,
    tempFilters,
  ]);

  // Закрытие фильтра дат при клике вне его
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedFilter === "date") {
        const target = event.target as Element;
        const isInsideDateFilter = target.closest('[data-filter="date"]');
        const isInsideAntPicker =
          target.closest(".ant-picker-dropdown") ||
          target.closest(".ant-picker") ||
          target.closest('[class*="ant-picker"]');

        if (!isInsideDateFilter && !isInsideAntPicker) {
          setExpandedFilter(null);
        }
      }
    };

    if (expandedFilter === "date") {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [expandedFilter]);

  const getFilterLabel = (key: string, value: string) => {
    switch (key) {
      case "status":
        return getStatusText(value);
      case "search":
        return value;
      case "method":
        return getMethodText(value);
      default:
        return value;
    }
  };

  if (error) {
    return (
      <OrdersContainer>
        <EmptyContainer>
          <h3>Ошибка загрузки</h3>
          <p>Не удалось загрузить список ордеров</p>
        </EmptyContainer>
      </OrdersContainer>
    );
  }

  return (
    <OrdersContainer>
      <Header>
        <HeaderLeft>
          <Subtitle>Управление ордерами и их статусами</Subtitle>
        </HeaderLeft>
      </Header>

      <FiltersContainer>
        <FiltersHeader onClick={() => setFiltersCollapsed(!filtersCollapsed)}>
          <FiltersTitle>
            Фильтры {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </FiltersTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {activeFiltersCount > 0 && (
              <ClearFilters onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}>Очистить все</ClearFilters>
            )}
            <FiltersToggleIcon isOpen={!filtersCollapsed}>▼</FiltersToggleIcon>
          </div>
        </FiltersHeader>

        <CollapsibleFilterBody isOpen={!filtersCollapsed}>
        <FilterTags>
          {/* Статус фильтр */}
          {filters.status || expandedFilter === "status" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "status"}
              hasContent={!!filters.status}
              onClick={() => toggleFilterExpansion("status")}
              data-filter="status"
            >
              <FilterContent>
                <span>Статус:</span>
                {expandedFilter === "status" ? (
                  <FilterSelect
                    value={tempFilters.status}
                    onChange={(e) => {
                      const newTempFilters = {
                        ...tempFilters,
                        status: e.target.value,
                      };
                      setTempFilters(newTempFilters);
                      // Для селектов применяем фильтр сразу
                      setFilters(newTempFilters);
                      setExpandedFilter(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    autoFocus
                  >
                    <option value="">Все статусы</option>
                    <option value="PENDING">Ожидает</option>
                    <option value="COMPLETED">Завершен</option>
                    <option value="CANCELLED">Отменен</option>
                    <option value="FAILED">Ошибка</option>
                    <option value="EXPIRED">Истек</option>
                  </FilterSelect>
                ) : (
                  <FilterValue>
                    {getFilterLabel("status", filters.status)}
                  </FilterValue>
                )}
                {filters.status && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter("status");
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("status")}>
              + Статус
            </AddFilterButton>
          )}

          {/* Поиск фильтр */}
          {filters.search || expandedFilter === "search" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "search"}
              hasContent={!!filters.search}
              onClick={() => toggleFilterExpansion("search")}
              data-filter="search"
            >
              <FilterContent>
                <span>Поиск:</span>
                {expandedFilter === "search" ? (
                  <FilterInput
                    type="text"
                    placeholder="ID ордера..."
                    value={tempFilters.search}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        search: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    autoFocus
                  />
                ) : (
                  <FilterValue>
                    {getFilterLabel("search", filters.search)}
                  </FilterValue>
                )}
                {filters.search && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter("search");
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("search")}>
              + Поиск
            </AddFilterButton>
          )}

          {/* Банк фильтр */}
          {filters.bank || expandedFilter === "bank" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "bank"}
              hasContent={!!filters.bank}
              onClick={() => toggleFilterExpansion("bank")}
              data-filter="bank"
            >
              <FilterContent>
                <span>Банк:</span>
                {expandedFilter === "bank" ? (
                  <FilterInput
                    type="text"
                    placeholder="Название банка..."
                    value={tempFilters.bank}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        bank: e.target.value,
                      })
                    }
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    autoFocus
                  />
                ) : (
                  <FilterValue>
                    {getFilterLabel("bank", filters.bank)}
                  </FilterValue>
                )}
                {filters.bank && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter("bank");
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("bank")}>
              + Банк
            </AddFilterButton>
          )}

          {/* Метод фильтр */}
          {filters.method || expandedFilter === "method" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "method"}
              hasContent={!!filters.method}
              onClick={() => toggleFilterExpansion("method")}
              data-filter="method"
            >
              <FilterContent>
                <span>Метод:</span>
                {expandedFilter === "method" ? (
                  <FilterSelect
                    value={tempFilters.method}
                    onChange={(e) => {
                      const newTempFilters = {
                        ...tempFilters,
                        method: e.target.value,
                      };
                      setTempFilters(newTempFilters);
                      // Для селектов применяем фильтр сразу
                      setFilters(newTempFilters);
                      setExpandedFilter(null);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    autoFocus
                  >
                    <option value="">Все методы</option>
                    <option value="sbp">СБП</option>
                    <option value="card">Карта</option>
                    <option value="account_transfer">Перевод на счёт</option>
                    <option value="transgov">TransGov</option>
                  </FilterSelect>
                ) : (
                  <FilterValue>
                    {getFilterLabel("method", filters.method)}
                  </FilterValue>
                )}
                {filters.method && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter("method");
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("method")}>
              + Метод
            </AddFilterButton>
          )}

          {/* Сумма RUB фильтр */}
          {filters.amountMin ||
          filters.amountMax ||
          expandedFilter === "amount" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "amount"}
              hasContent={!!(filters.amountMin || filters.amountMax)}
              onClick={() => toggleFilterExpansion("amount")}
              data-filter="amount"
            >
              <FilterContent>
                <span>Сумма (RUB):</span>
                {expandedFilter === "amount" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FilterInput
                      type="number"
                      placeholder="От"
                      value={tempFilters.amountMin}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          amountMin: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      autoFocus
                    />
                    <span style={{ color: "var(--text-muted)" }}>—</span>
                    <FilterInput
                      type="number"
                      placeholder="До"
                      value={tempFilters.amountMax}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          amountMax: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                    />
                  </div>
                ) : (
                  <FilterValue>
                    {filters.amountMin && `От: ${filters.amountMin}₽`}{" "}
                    {filters.amountMax && `До: ${filters.amountMax}₽`}
                  </FilterValue>
                )}
                {(filters.amountMin || filters.amountMax) && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      const newFilters = {
                        ...filters,
                        amountMin: "",
                        amountMax: "",
                      };
                      setFilters(newFilters);
                      setTempFilters(newFilters);
                      setExpandedFilter(null);
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("amount")}>
              + Сумма (RUB)
            </AddFilterButton>
          )}



          {/* Дата фильтр - ТОЛЬКО ЗДЕСЬ используем AntDateRangePicker */}
          {filters.dateFrom || filters.dateTo || expandedFilter === "date" ? (
            <DateFilterTag
              isActive={true}
              isExpanded={expandedFilter === "date"}
              hasContent={!!(filters.dateFrom || filters.dateTo)}
              onClick={() => toggleFilterExpansion("date")}
              data-filter="date"
            >
              <FilterContent>
                <span>Период:</span>
                {expandedFilter === "date" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.25rem 0",
                      minHeight: "32px",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{
                        display: "flex",
                    alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <AntDateRangePicker
                        key={`${tempFilters.dateFrom}-${tempFilters.dateTo}`}
                        dateFromValue={tempFilters.dateFrom}
                        dateToValue={tempFilters.dateTo}
                        timeFromValue={tempFilters.timeFrom}
                        timeToValue={tempFilters.timeTo}
                        onDateFromChange={(date) =>
                          setTempFilters({
                            ...tempFilters,
                            dateFrom: date,
                          })
                        }
                        onDateToChange={(date) =>
                          setTempFilters({
                            ...tempFilters,
                            dateTo: date,
                          })
                        }
                        onTimeFromChange={(time) =>
                          setTempFilters({
                            ...tempFilters,
                            timeFrom: time,
                          })
                        }
                        onTimeToChange={(time) =>
                          setTempFilters({
                            ...tempFilters,
                            timeTo: time,
                          })
                        }
                        placeholder={[
                          "Дата и время начала",
                          "Дата и время окончания",
                        ]}
                        onOk={(dateFrom, dateTo, timeFrom, timeTo) => {
                          // Очищаем debounce таймер
                          if (debounceTimeoutRef.current) {
                            clearTimeout(debounceTimeoutRef.current);
                          }

                          // Обновляем tempFilters с новыми значениями
                          const newTempFilters = {
                            ...tempFilters,
                            dateFrom,
                            dateTo,
                            timeFrom,
                            timeTo,
                          };
                          setTempFilters(newTempFilters);

                          // Применяем фильтр и закрываем только если выбраны ОБЕ даты
                          if (dateFrom && dateTo) {
                            setFilters(newTempFilters);
                            setTimeout(() => {
                              setExpandedFilter(null);
                            }, 100);
                          }
                          // Если выбрана только одна дата, фильтр остается открытым
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <FilterValue>
                    {filters.dateFrom &&
                      `${new Date(filters.dateFrom).toLocaleDateString("ru-RU")}${filters.timeFrom ? ` ${filters.timeFrom}` : ""}`}{" "}
                    {filters.dateTo &&
                      `— ${new Date(filters.dateTo).toLocaleDateString("ru-RU")}${filters.timeTo ? ` ${filters.timeTo}` : ""}`}
                  </FilterValue>
                )}
                {(filters.dateFrom || filters.dateTo) && (
                  <CloseIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      const newFilters = {
                        ...filters,
                        dateFrom: "",
                        dateTo: "",
                        timeFrom: "",
                        timeTo: "",
                      };
                      setFilters(newFilters);
                      setTempFilters(newFilters);
                      setExpandedFilter(null);
                    }}
                  >
                    ×
                  </CloseIcon>
                )}
              </FilterContent>
            </DateFilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("date")}>
              + Дата
            </AddFilterButton>
          )}
        </FilterTags>
        </CollapsibleFilterBody>
      </FiltersContainer>

      <OrdersCard>
        <TableHeaderSection>
          <TableTitle>Список ордеров</TableTitle>
          <TableRefreshButton onClick={() => refetch()} disabled={isFetching}>
            <RefreshSvgIcon
              spinning={isFetching}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M3 22v-6h6" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
            </RefreshSvgIcon>
            Обновить
          </TableRefreshButton>
        </TableHeaderSection>

        <TableContainer>
          <Table>
            <thead>
              <tr>
                <TableHeader>#ID</TableHeader>
                <TableHeader>Дата создания</TableHeader>
                <TableHeader>Дата оплаты</TableHeader>
                <TableHeader>Метод</TableHeader>
                <TableHeader>Сумма</TableHeader>
                <TableHeader>Курс</TableHeader>
                <TableHeader>Сумма (USDT)</TableHeader>
                <TableHeader>Статус</TableHeader>
                <TableHeader>Реквизит</TableHeader>
                <TableHeader>До отмены</TableHeader>
                <TableHeader>Действия</TableHeader>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <TableCell colSpan={11}>
                    <LoadingContainer>Загрузка ордеров...</LoadingContainer>
                  </TableCell>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <TableCell colSpan={11}>
                    <EmptyContainer>
                      <h3>Ордеры не найдены</h3>
                      <p>Попробуйте изменить параметры фильтрации</p>
                    </EmptyContainer>
                  </TableCell>
                </tr>
              ) : (
                orders.map((order: any) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <OrderId onClick={() => handleOrderClick(order.id)}>
                        #{order.invId}
                      </OrderId>
                    </TableCell>
                    <TableCell>{formatDate(order.createdAt)}</TableCell>
                    <TableCell>
                      {order.completedAt
                        ? formatDate(order.completedAt)
                        : "—"}
                    </TableCell>
                    <TableCell>{getPaymentMethod(order)}</TableCell>
                    <TableCell>{formatRUB(order.amount)}</TableCell>
                    <TableCell>{order.exchangeRate.toFixed(2)}</TableCell>
                    <TableCell>{formatUSDT(order.usdtAmount)}</TableCell>
                    <TableCell>
                      <StatusBadge status={order.status}>
                        {getStatusText(order.status)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {order.requisite?.tag && (
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{order.requisite.tag}</span>
                        )}
                        <span style={{ color: 'var(--text-secondary)' }}>{order.requisite?.bank || order.bank || "—"}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {order.requisite?.cardNumber || order.requisite?.sbpPhoneNumber || order.requisite?.phoneNumber || order.requisite?.accountNumber || order.cardNumber || order.phoneNumber || "—"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {["PENDING", "PROCESSING"].includes(order.status) ? (
                        <CountdownTimer expiresAt={order.expiresAt} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {["PENDING", "EXPIRED", "CANCELLED"].includes(order.status) && 
                       profile?.canConfirmManually && 
                       (Date.now() - new Date(order.createdAt).getTime()) / (1000 * 60 * 60) <= 48 ? (
                        <ConfirmButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleManualConfirm(order.id);
                          }}
                          disabled={confirmingOrderId === order.id}
                          title="Ручное подтверждение"
                        >
                          {confirmingOrderId === order.id ? "..." : "Подтвердить"}
                        </ConfirmButton>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

        {/* Mobile Order Cards */}
        <MobileOrdersList>
          {isLoading ? (
            <LoadingContainer>Загрузка ордеров...</LoadingContainer>
          ) : orders.length === 0 ? (
            <EmptyContainer>
              <h3>Ордеры не найдены</h3>
              <p>Попробуйте изменить параметры фильтрации</p>
            </EmptyContainer>
          ) : (
            orders.map((order: any) => (
              <MobileOrderCard key={order.id} onClick={() => handleOrderClick(order.id)}>
                <MobileOrderHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    <MobileOrderId>#{order.invId}</MobileOrderId>
                    <span style={{ fontSize: '0.5625rem', color: 'var(--text-muted)' }}>{formatDate(order.createdAt)}</span>
                  </div>
                  <StatusBadge status={order.status}>
                    {getStatusText(order.status)}
                  </StatusBadge>
                </MobileOrderHeader>
                <MobileOrderBody>
                  <MobileOrderField>
                    <MobileOrderValue>{formatRUB(order.amount)}</MobileOrderValue>
                  </MobileOrderField>
                  <MobileOrderField>
                    <MobileOrderLabel>→</MobileOrderLabel>
                    <MobileOrderValue>{formatUSDT(order.usdtAmount)}</MobileOrderValue>
                  </MobileOrderField>
                  <MobileOrderField>
                    <MobileOrderValue style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{getPaymentMethod(order)}</MobileOrderValue>
                  </MobileOrderField>
                  {order.requisite?.tag && (
                    <MobileOrderField>
                      <MobileOrderValue style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{order.requisite.tag}</MobileOrderValue>
                    </MobileOrderField>
                  )}
                  <MobileOrderField>
                    <MobileOrderValue style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{order.requisite?.bank || order.bank || "—"}</MobileOrderValue>
                  </MobileOrderField>
                  <MobileOrderField>
                    <MobileOrderValue style={{ fontFamily: 'monospace', fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                      {order.requisite?.cardNumber || order.requisite?.sbpPhoneNumber || order.requisite?.phoneNumber || order.requisite?.accountNumber || order.cardNumber || order.phoneNumber || "—"}
                    </MobileOrderValue>
                  </MobileOrderField>
                </MobileOrderBody>
                {(["PENDING", "PROCESSING"].includes(order.status) || 
                  (["PENDING", "EXPIRED", "CANCELLED"].includes(order.status) && profile?.canConfirmManually && (Date.now() - new Date(order.createdAt).getTime()) / (1000 * 60 * 60) <= 48)) && (
                <MobileOrderFooter>
                  {["PENDING", "PROCESSING"].includes(order.status) && (
                    <span style={{ fontSize: '0.625rem' }}>
                      <CountdownTimer expiresAt={order.expiresAt} />
                    </span>
                  )}
                  {["PENDING", "EXPIRED", "CANCELLED"].includes(order.status) && 
                   profile?.canConfirmManually && 
                   (Date.now() - new Date(order.createdAt).getTime()) / (1000 * 60 * 60) <= 48 && (
                    <ConfirmButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualConfirm(order.id);
                      }}
                      disabled={confirmingOrderId === order.id}
                    >
                      {confirmingOrderId === order.id ? "..." : "Подтвердить"}
                    </ConfirmButton>
                  )}
                </MobileOrderFooter>
                )}
              </MobileOrderCard>
            ))
          )}
        </MobileOrdersList>
      </OrdersCard>


      {/* Pagination */}
      {ordersData?.meta && ordersData.meta.totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.5rem",
            marginTop: "1rem",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: "0.5rem 0.75rem",
              border: "1px solid var(--border-color)",
              borderRadius: "0.375rem",
              background: page === 1 ? "var(--bg-secondary)" : "var(--bg-card)",
              cursor: page === 1 ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
            }}
          >
            ←
          </button>
          <span style={{ display: "flex", alignItems: "center", fontSize: "0.875rem", color: "var(--text-primary)" }}>
            {page} / {ordersData.meta.totalPages}
          </span>
          <button
            onClick={() =>
              setPage((p) => Math.min(ordersData.meta.totalPages, p + 1))
            }
            disabled={page === ordersData.meta.totalPages}
            style={{
              padding: "0.5rem 0.75rem",
              border: "1px solid var(--border-color)",
              borderRadius: "0.375rem",
              background:
                page === ordersData.meta.totalPages ? "var(--bg-secondary)" : "var(--bg-card)",
              cursor:
                page === ordersData.meta.totalPages ? "not-allowed" : "pointer",
              fontSize: "0.875rem",
            }}
          >
            →
          </button>
        </div>
      )}
    </OrdersContainer>
  );
};

export default Orders;
